import React from 'react';

/**
 * Badge - colored pill used for status, risk, role, severity chips.
 *
 * Props:
 *   label     - string to display
 *   color     - one of: gray | indigo | green | amber | red | blue | slate
 *               (legacy - keep for back-compat with existing call sites)
 *   severity  - one of: CRITICAL | HIGH | MEDIUM | LOW
 *               (overrides `color`; uses spec §12 severity tokens)
 *   risk      - numeric 0–100
 *               (overrides `color` + `severity`; auto-picks the band)
 *   size      - sm (default) | xs
 */

const COLOR_STYLES = {
  gray:   'bg-slate-100  text-slate-700  ring-slate-200',
  slate:  'bg-slate-100  text-slate-700  ring-slate-200',
  indigo: 'bg-teal-50    text-teal-800   ring-teal-200',
  teal:   'bg-teal-50    text-teal-800   ring-teal-200',
  navy:   'bg-slate-100  text-slate-800  ring-slate-200',
  green:  'bg-green-50   text-green-700  ring-green-200',
  amber:  'bg-amber-50   text-amber-700  ring-amber-200',
  red:    'bg-red-50     text-red-700    ring-red-200',
  blue:   'bg-teal-50    text-teal-800   ring-teal-200',
};

// Severity tokens - solid background per spec §12 (e.g. red-600/red-100).
const SEVERITY_STYLES = {
  CRITICAL: 'bg-severity-critical-bg text-severity-critical-fg ring-red-700',
  HIGH:     'bg-severity-high-bg     text-severity-high-fg     ring-orange-600',
  MEDIUM:   'bg-severity-medium-bg   text-severity-medium-fg   ring-amber-500',
  LOW:      'bg-severity-low-bg      text-severity-low-fg      ring-slate-500',
};

function riskBand(score) {
  if (score >= 81) return { label: 'CRITICAL', cls: 'bg-risk-critical text-white ring-red-700' };
  if (score >= 61) return { label: 'HIGH',     cls: 'bg-risk-high     text-white ring-orange-600' };
  if (score >= 31) return { label: 'MEDIUM',   cls: 'bg-risk-medium   text-white ring-amber-500' };
  return                  { label: 'LOW',      cls: 'bg-risk-low      text-white ring-emerald-600' };
}

export default function Badge({ label, color = 'gray', severity, risk, size = 'sm', className = '' }) {
  let palette;
  let displayLabel = label;

  if (typeof risk === 'number') {
    const band = riskBand(risk);
    palette = band.cls;
    if (displayLabel === undefined) displayLabel = `${Math.round(risk)} · ${band.label}`;
  } else if (severity && SEVERITY_STYLES[severity]) {
    palette = SEVERITY_STYLES[severity];
    if (displayLabel === undefined) displayLabel = severity;
  } else {
    palette = COLOR_STYLES[color] || COLOR_STYLES.gray;
  }

  const sizing = size === 'xs'
    ? 'px-1.5 py-0.5 text-[10px]'
    : 'px-2 py-0.5 text-xs';

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ring-1 ring-inset ${palette} ${sizing} ${className}`}
    >
      {displayLabel}
    </span>
  );
}
